import React, { useMemo } from "react";
import { HorizontalBar } from "react-chartjs-2";
import PropTypes from "prop-types";
import { Box, Grid, Paper, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { theme as appTheme } from "../../../assets/theme/StyledTheme";

/** Intervalos do eixo: 0-2, 2-4, 4-6, 6-8, 8-10 */
export function gradeToDistributionBinIndex(grade) {
  if (grade == null || Number.isNaN(Number(grade))) return -1;
  const g = Number(grade);
  if (g < 0) return 0;
  if (g < 2) return 0;
  if (g < 4) return 1;
  if (g < 6) return 2;
  if (g < 8) return 3;
  return 4;
}

function formatGrade(grade, locale) {
  return Number(grade).toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
}

function intervalLabelForGrade(grade, labels) {
  const idx = gradeToDistributionBinIndex(grade);
  if (idx < 0 || !labels || !labels[idx]) return "—";
  return labels[idx];
}

function formatDistributionTooltipLabel(tooltipItem, data, t) {
  const idx = tooltipItem.index;
  const intervalLabel =
    data.labels[idx] != null ? String(data.labels[idx]) : "";
  let v = tooltipItem.xLabel;
  if (v == null) v = tooltipItem.yLabel;
  if (v == null) v = tooltipItem.value;
  const n = Number(v);
  const count = Number.isFinite(n) ? Math.round(n) : 0;
  const countLabel = t(
    count === 1
      ? "student.charts.studentsCountOne"
      : "student.charts.studentsCountMany",
    { n: count, range: intervalLabel }
  );
  const ds = data.datasets[tooltipItem.datasetIndex];
  const name = ds && ds.label ? ds.label : "";
  const multiSeries = data.datasets && data.datasets.length > 1;
  if (multiSeries && name) {
    return t("student.charts.seriesWithCount", {
      name,
      countLabel
    });
  }
  return countLabel;
}

const HIGHLIGHT_BACKGROUND = "rgba(255, 152, 0, 0.95)";
const HIGHLIGHT_BORDER = "#ef6c00";

function buildStudentGradesSummary(datasets, t, locale) {
  if (!datasets || !datasets.length) return null;
  const withGrade = datasets.filter(d => d.studentGrade != null);
  if (!withGrade.length) return null;
  if (withGrade.length === 1) {
    return t("student.charts.studentGrade", {
      grade: formatGrade(withGrade[0].studentGrade, locale)
    });
  }
  const parts = withGrade.map(d => {
    const short =
      d.label && d.label.includes(" · ")
        ? d.label.split(" · ")[0]
        : d.label || "";
    return `${short}: ${formatGrade(d.studentGrade, locale)}`;
  });
  return t("student.charts.studentGrades", { grades: parts.join(" · ") });
}

function applyStudentBinHighlight(dataset) {
  const { studentGrade, data, backgroundColor, borderColor } = dataset;
  if (studentGrade == null || !Array.isArray(data)) {
    return dataset;
  }
  const binIdx = gradeToDistributionBinIndex(studentGrade);
  if (binIdx < 0 || binIdx >= data.length) {
    return dataset;
  }
  const baseBg =
    typeof backgroundColor === "string"
      ? backgroundColor
      : appTheme.color.primary.main;
  const baseBorder = typeof borderColor === "string" ? borderColor : baseBg;

  return {
    ...dataset,
    backgroundColor: data.map((_, i) =>
      i === binIdx ? HIGHLIGHT_BACKGROUND : baseBg
    ),
    borderColor: data.map((_, i) =>
      i === binIdx ? HIGHLIGHT_BORDER : baseBorder
    ),
    borderWidth: data.map((_, i) => (i === binIdx ? 3 : 0))
  };
}

function buildChartData(raw) {
  if (!raw || !raw.datasets) {
    return raw;
  }
  return {
    ...raw,
    datasets: raw.datasets.map(ds => applyStudentBinHighlight(ds))
  };
}

function StudentGradeCallout({
  datasets,
  labels,
  periodLabel,
  disciplineLabel
}) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";
  const withGrade = (datasets || []).filter(d => d.studentGrade != null);
  if (!withGrade.length || !labels) {
    return null;
  }

  const context = [periodLabel, disciplineLabel].filter(Boolean).join(" · ");

  return (
    <Box mb={2}>
      {context && (
        <Typography
          variant="subtitle2"
          color="textSecondary"
          component="p"
          gutterBottom
          style={{ fontWeight: 600, letterSpacing: 0.2 }}
        >
          {context}
        </Typography>
      )}
      {withGrade.length === 1 ? (
        <Paper
          elevation={0}
          style={{
            border: `1px solid ${theme.palette.divider}`,
            borderLeft: `5px solid ${theme.palette.primary.main}`,
            padding: theme.spacing(2.5),
            backgroundColor:
              theme.palette.type === "dark"
                ? "rgba(255,255,255,0.05)"
                : theme.palette.primary.light
          }}
        >
          <Typography
            variant="overline"
            color="textSecondary"
            display="block"
            style={{ fontWeight: 600 }}
          >
            {t("student.charts.studentGradeLabel")}
          </Typography>
          <Typography
            variant="h3"
            color="primary"
            component="p"
            style={{
              fontWeight: 700,
              lineHeight: 1.15,
              marginTop: 4,
              marginBottom: 8
            }}
          >
            {formatGrade(withGrade[0].studentGrade, locale)}
          </Typography>
          <Typography variant="body2" color="textSecondary" component="p">
            {t("student.charts.histogramPosition", {
              interval: intervalLabelForGrade(withGrade[0].studentGrade, labels)
            })}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {withGrade.map(ds => (
            <Grid item xs={12} sm={6} key={ds.label || String(ds.periodId)}>
              <Paper
                elevation={0}
                style={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  padding: theme.spacing(2)
                }}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  {ds.label || t("student.charts.series")}
                </Typography>
                <Typography
                  variant="h4"
                  color="primary"
                  style={{ fontWeight: 700 }}
                >
                  {formatGrade(ds.studentGrade, locale)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("student.charts.interval", {
                    interval: intervalLabelForGrade(ds.studentGrade, labels)
                  })}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

StudentGradeCallout.propTypes = {
  datasets: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      studentGrade: PropTypes.number,
      periodId: PropTypes.string
    })
  ),
  labels: PropTypes.arrayOf(PropTypes.string),
  periodLabel: PropTypes.string,
  disciplineLabel: PropTypes.string
};

StudentGradeCallout.defaultProps = {
  datasets: [],
  labels: [],
  periodLabel: "",
  disciplineLabel: ""
};

const GRADE_DISTRIBUTION_CHART_HEIGHT = 360;

const datasetKeyProvider = () => {
  return btoa(Math.random()).substring(0, 120);
};

const GradeDistributionChart = ({ data, periodLabel, disciplineLabel }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";
  const chartData = useMemo(() => buildChartData(data), [data]);

  const fallbackSummary = useMemo(
    () =>
      data && data.datasets
        ? buildStudentGradesSummary(data.datasets, t, locale)
        : null,
    [data, t, locale]
  );

  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      legend: {
        display: true,
        position: "bottom",
        labels: {
          fontSize: 14,
          padding: 16,
          usePointStyle: true,
          boxWidth: 8
        }
      },
      scales: {
        xAxes: [
          {
            ticks: {
              fontSize: 12,
              fontColor: "#444"
            },
            gridLines: {
              color: "rgba(0, 0, 0, 0.08)"
            }
          }
        ],
        yAxes: [
          {
            ticks: {
              fontSize: 12,
              fontColor: "#444"
            },
            gridLines: {
              display: false
            }
          }
        ]
      },
      tooltips: {
        mode: "index",
        intersect: false,
        titleFontSize: 0,
        titleMarginBottom: 0,
        callbacks: {
          title() {
            return "";
          },
          label(tooltipItem, chartDataArg) {
            return formatDistributionTooltipLabel(tooltipItem, chartDataArg, t);
          },
          afterLabel(tooltipItem, chartDataArg) {
            const ds = chartDataArg.datasets[tooltipItem.datasetIndex];
            if (
              ds == null ||
              ds.studentGrade == null ||
              tooltipItem.index !== gradeToDistributionBinIndex(ds.studentGrade)
            ) {
              return undefined;
            }
            return t("student.charts.studentGrade", {
              grade: formatGrade(ds.studentGrade, locale)
            });
          }
        }
      }
    }),
    [t, locale]
  );

  const hasCalloutGrades =
    data && data.datasets && data.datasets.some(d => d.studentGrade != null);

  return (
    <div>
      <StudentGradeCallout
        datasets={data && data.datasets}
        labels={data && data.labels}
        periodLabel={periodLabel}
        disciplineLabel={disciplineLabel}
      />
      {!hasCalloutGrades && fallbackSummary && (
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            {fallbackSummary}
          </Typography>
        </Box>
      )}
      <Box width="100%" style={{ height: GRADE_DISTRIBUTION_CHART_HEIGHT }}>
        <HorizontalBar
          data={chartData}
          options={chartOptions}
          base={10}
          height={GRADE_DISTRIBUTION_CHART_HEIGHT}
          datasetKeyProvider={datasetKeyProvider}
        />
      </Box>
    </div>
  );
};

GradeDistributionChart.propTypes = {
  data: PropTypes.shape({
    datasets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number),
        studentGrade: PropTypes.number
      })
    ),
    labels: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  periodLabel: PropTypes.string,
  disciplineLabel: PropTypes.string
};

GradeDistributionChart.defaultProps = {
  periodLabel: "",
  disciplineLabel: ""
};

export default GradeDistributionChart;
